/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 * 
 * src/services/landingPageConfigService.ts
 * 
 * Serviço para leitura, observação e persistência da configuração da Landing Page no Firestore.
 * Coleção centralizada: system_settings/landing_page_config
 * Suporta resiliência campo a campo para 2A, 2B, 2C, 2D e 2E (Contato, CTA Final e Rodapé).
 */

import { doc, getDoc, setDoc, onSnapshot } from "firebase/firestore";
import { db } from "../utils/firebase";
import {
  LandingPageConfig,
  ModuleItemConfig,
  CommercialModelItemConfig,
  CommercialModelsConfig,
  BenefitItemConfig,
  BenefitsConfig,
  DEFAULT_LANDING_PAGE_CONFIG,
} from "../types/landingPageConfig";

const CONFIG_COLLECTION = "system_settings";
const CONFIG_DOC_ID = "landing_page_config";

/**
 * Obtém a referência do documento de configuração no Firestore.
 */
function getLandingPageDocRef() {
  return doc(db, CONFIG_COLLECTION, CONFIG_DOC_ID);
}

/**
 * Normaliza os módulos mesclando itens salvos e os defaults por ID estável.
 */
function normalizeModules(rawModules?: any[]): ModuleItemConfig[] {
  if (!Array.isArray(rawModules) || rawModules.length === 0) {
    return DEFAULT_LANDING_PAGE_CONFIG.modules;
  }

  const savedMap = new Map<string, any>();
  rawModules.forEach((m) => {
    if (m && typeof m.id === "string") {
      savedMap.set(m.id, m);
    }
  });

  return DEFAULT_LANDING_PAGE_CONFIG.modules.map((defMod) => {
    const savedMod = savedMap.get(defMod.id);
    if (!savedMod) return defMod;

    return {
      id: defMod.id,
      title: typeof savedMod.title === "string" && savedMod.title.trim() ? savedMod.title : defMod.title,
      description: typeof savedMod.description === "string" && savedMod.description.trim() ? savedMod.description : defMod.description,
      enabled: typeof savedMod.enabled === "boolean" ? savedMod.enabled : true,
    };
  });
}

/**
 * Normaliza os modelos comerciais mesclando itens salvos com os defaults por ID estável.
 */
function normalizeCommercialModels(raw?: any): CommercialModelsConfig {
  if (!raw || typeof raw !== "object") {
    return DEFAULT_LANDING_PAGE_CONFIG.commercialModels;
  }

  const title = typeof raw.title === "string" && raw.title.trim()
    ? raw.title
    : DEFAULT_LANDING_PAGE_CONFIG.commercialModels.title;

  const description = typeof raw.description === "string" && raw.description.trim()
    ? raw.description
    : DEFAULT_LANDING_PAGE_CONFIG.commercialModels.description;

  const rawModels = Array.isArray(raw.models) ? raw.models : [];
  const savedMap = new Map<string, any>();
  rawModels.forEach((m) => {
    if (m && typeof m.id === "string") {
      savedMap.set(m.id, m);
    }
  });

  const models: CommercialModelItemConfig[] = DEFAULT_LANDING_PAGE_CONFIG.commercialModels.models.map((defM) => {
    const savedM = savedMap.get(defM.id);
    if (!savedM) return defM;

    return {
      id: defM.id,
      title: typeof savedM.title === "string" && savedM.title.trim() ? savedM.title : defM.title,
      subtitle: typeof savedM.subtitle === "string" && savedM.subtitle.trim() ? savedM.subtitle : defM.subtitle,
      description: typeof savedM.description === "string" && savedM.description.trim() ? savedM.description : defM.description,
      enabled: typeof savedM.enabled === "boolean" ? savedM.enabled : true,
    };
  });

  return { title, description, models };
}

/**
 * Normaliza a seção de benefícios mesclando itens salvos com os defaults por ID estável.
 */
function normalizeBenefits(raw?: any): BenefitsConfig {
  if (!raw || typeof raw !== "object") {
    return DEFAULT_LANDING_PAGE_CONFIG.benefits;
  }

  const title = typeof raw.title === "string" && raw.title.trim()
    ? raw.title
    : DEFAULT_LANDING_PAGE_CONFIG.benefits.title;

  const description = typeof raw.description === "string" && raw.description.trim()
    ? raw.description
    : DEFAULT_LANDING_PAGE_CONFIG.benefits.description;

  const rawItems = Array.isArray(raw.items) ? raw.items : [];
  const savedMap = new Map<string, any>();
  rawItems.forEach((b) => {
    if (b && typeof b.id === "string") {
      savedMap.set(b.id, b);
    }
  });

  const items: BenefitItemConfig[] = DEFAULT_LANDING_PAGE_CONFIG.benefits.items.map((defB) => {
    const savedB = savedMap.get(defB.id);
    if (!savedB) return defB;

    return {
      id: defB.id,
      title: typeof savedB.title === "string" && savedB.title.trim() ? savedB.title : defB.title,
      description: typeof savedB.description === "string" && savedB.description.trim() ? savedB.description : defB.description,
      enabled: typeof savedB.enabled === "boolean" ? savedB.enabled : true,
    };
  });

  return { title, description, items };
}

/**
 * Mescla dados do Firestore com o fallback padrão de forma resiliente campo a campo.
 */
function mergeWithFallback(data?: any): LandingPageConfig {
  if (!data) return DEFAULT_LANDING_PAGE_CONFIG;

  return {
    institutional: {
      brandName:
        data?.institutional?.brandName ||
        DEFAULT_LANDING_PAGE_CONFIG.institutional.brandName,
      officialEmail:
        data?.institutional?.officialEmail ||
        DEFAULT_LANDING_PAGE_CONFIG.institutional.officialEmail,
      cnpj:
        data?.institutional?.cnpj ||
        DEFAULT_LANDING_PAGE_CONFIG.institutional.cnpj,
    },
    hero: {
      eyebrow:
        data?.hero?.eyebrow ||
        DEFAULT_LANDING_PAGE_CONFIG.hero.eyebrow,
      title:
        data?.hero?.title ||
        DEFAULT_LANDING_PAGE_CONFIG.hero.title,
      subtitle:
        data?.hero?.subtitle ||
        DEFAULT_LANDING_PAGE_CONFIG.hero.subtitle,
      primaryCtaLabel:
        data?.hero?.primaryCtaLabel ||
        DEFAULT_LANDING_PAGE_CONFIG.hero.primaryCtaLabel,
      arenaCtaLabel:
        data?.hero?.arenaCtaLabel ||
        DEFAULT_LANDING_PAGE_CONFIG.hero.arenaCtaLabel,
      capabilityTitle:
        data?.hero?.capabilityTitle ||
        DEFAULT_LANDING_PAGE_CONFIG.hero.capabilityTitle,
      capabilityDescription:
        data?.hero?.capabilityDescription ||
        DEFAULT_LANDING_PAGE_CONFIG.hero.capabilityDescription,
    },
    audiences: {
      athlete: {
        title:
          data?.audiences?.athlete?.title ||
          DEFAULT_LANDING_PAGE_CONFIG.audiences.athlete.title,
        description:
          data?.audiences?.athlete?.description ||
          DEFAULT_LANDING_PAGE_CONFIG.audiences.athlete.description,
        ctaLabel:
          data?.audiences?.athlete?.ctaLabel ||
          DEFAULT_LANDING_PAGE_CONFIG.audiences.athlete.ctaLabel,
      },
      arenaOwner: {
        title:
          data?.audiences?.arenaOwner?.title ||
          DEFAULT_LANDING_PAGE_CONFIG.audiences.arenaOwner.title,
        description:
          data?.audiences?.arenaOwner?.description ||
          DEFAULT_LANDING_PAGE_CONFIG.audiences.arenaOwner.description,
        ctaLabel:
          data?.audiences?.arenaOwner?.ctaLabel ||
          DEFAULT_LANDING_PAGE_CONFIG.audiences.arenaOwner.ctaLabel,
      },
    },
    about: {
      title:
        data?.about?.title ||
        DEFAULT_LANDING_PAGE_CONFIG.about.title,
      description:
        data?.about?.description ||
        DEFAULT_LANDING_PAGE_CONFIG.about.description,
    },
    objective: {
      title:
        data?.objective?.title ||
        DEFAULT_LANDING_PAGE_CONFIG.objective.title,
      description:
        data?.objective?.description ||
        DEFAULT_LANDING_PAGE_CONFIG.objective.description,
    },
    modules: normalizeModules(data?.modules),
    commercialModels: normalizeCommercialModels(data?.commercialModels),
    benefits: normalizeBenefits(data?.benefits),
    contact: {
      title:
        data?.contact?.title ||
        DEFAULT_LANDING_PAGE_CONFIG.contact.title,
      description:
        data?.contact?.description ||
        DEFAULT_LANDING_PAGE_CONFIG.contact.description,
    },
    finalCta: {
      title:
        data?.finalCta?.title ||
        DEFAULT_LANDING_PAGE_CONFIG.finalCta.title,
      subtitle:
        data?.finalCta?.subtitle ||
        DEFAULT_LANDING_PAGE_CONFIG.finalCta.subtitle,
    },
    footer: {
      description:
        data?.footer?.description ||
        DEFAULT_LANDING_PAGE_CONFIG.footer.description,
      copyright:
        data?.footer?.copyright ||
        DEFAULT_LANDING_PAGE_CONFIG.footer.copyright,
    },
    updatedAt: data?.updatedAt,
    updatedBy: data?.updatedBy,
  };
}

export const LandingPageConfigService = {
  /**
   * Lê a configuração atual do Firestore com fallback de segurança.
   */
  async getLandingPageConfig(): Promise<LandingPageConfig> {
    try {
      const docRef = getLandingPageDocRef();
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        return mergeWithFallback(docSnap.data());
      }
      return DEFAULT_LANDING_PAGE_CONFIG;
    } catch (error) {
      console.warn(
        "[LandingPageConfigService] Falha ao ler do Firestore. Usando fallback padrão.",
        error
      );
      return DEFAULT_LANDING_PAGE_CONFIG;
    }
  },

  /**
   * Subscreve em tempo real às alterações da configuração no Firestore.
   */
  subscribeLandingPageConfig(
    callback: (config: LandingPageConfig) => void
  ): () => void {
    try {
      const docRef = getLandingPageDocRef();
      return onSnapshot(
        docRef,
        (snapshot) => {
          if (snapshot.exists()) {
            callback(mergeWithFallback(snapshot.data()));
          } else {
            callback(DEFAULT_LANDING_PAGE_CONFIG);
          }
        },
        (error) => {
          console.warn(
            "[LandingPageConfigService] Erro na subscrição do Firestore. Usando fallback.",
            error
          );
          callback(DEFAULT_LANDING_PAGE_CONFIG);
        }
      );
    } catch (error) {
      console.warn(
        "[LandingPageConfigService] Falha ao iniciar listener. Usando fallback.",
        error
      );
      callback(DEFAULT_LANDING_PAGE_CONFIG);
      return () => {};
    }
  },

  /**
   * Salva as configurações no Firestore (Apenas administradores autenticados).
   */
  async saveLandingPageConfig(
    config: LandingPageConfig,
    updatedByEmail?: string
  ): Promise<boolean> {
    try {
      const docRef = getLandingPageDocRef();
      const payload: LandingPageConfig = {
        institutional: {
          brandName: config.institutional.brandName.trim() || DEFAULT_LANDING_PAGE_CONFIG.institutional.brandName,
          officialEmail: config.institutional.officialEmail.trim() || DEFAULT_LANDING_PAGE_CONFIG.institutional.officialEmail,
          cnpj: config.institutional.cnpj.trim() || DEFAULT_LANDING_PAGE_CONFIG.institutional.cnpj,
        },
        hero: {
          eyebrow: config.hero.eyebrow.trim() || DEFAULT_LANDING_PAGE_CONFIG.hero.eyebrow,
          title: config.hero.title.trim() || DEFAULT_LANDING_PAGE_CONFIG.hero.title,
          subtitle: config.hero.subtitle.trim() || DEFAULT_LANDING_PAGE_CONFIG.hero.subtitle,
          primaryCtaLabel: config.hero.primaryCtaLabel.trim() || DEFAULT_LANDING_PAGE_CONFIG.hero.primaryCtaLabel,
          arenaCtaLabel: config.hero.arenaCtaLabel.trim() || DEFAULT_LANDING_PAGE_CONFIG.hero.arenaCtaLabel,
          capabilityTitle: config.hero.capabilityTitle.trim() || DEFAULT_LANDING_PAGE_CONFIG.hero.capabilityTitle,
          capabilityDescription: config.hero.capabilityDescription.trim() || DEFAULT_LANDING_PAGE_CONFIG.hero.capabilityDescription,
        },
        audiences: {
          athlete: {
            title: config.audiences.athlete.title.trim() || DEFAULT_LANDING_PAGE_CONFIG.audiences.athlete.title,
            description: config.audiences.athlete.description.trim() || DEFAULT_LANDING_PAGE_CONFIG.audiences.athlete.description,
            ctaLabel: config.audiences.athlete.ctaLabel.trim() || DEFAULT_LANDING_PAGE_CONFIG.audiences.athlete.ctaLabel,
          },
          arenaOwner: {
            title: config.audiences.arenaOwner.title.trim() || DEFAULT_LANDING_PAGE_CONFIG.audiences.arenaOwner.title,
            description: config.audiences.arenaOwner.description.trim() || DEFAULT_LANDING_PAGE_CONFIG.audiences.arenaOwner.description,
            ctaLabel: config.audiences.arenaOwner.ctaLabel.trim() || DEFAULT_LANDING_PAGE_CONFIG.audiences.arenaOwner.ctaLabel,
          },
        },
        about: {
          title: config.about.title.trim() || DEFAULT_LANDING_PAGE_CONFIG.about.title,
          description: config.about.description.trim() || DEFAULT_LANDING_PAGE_CONFIG.about.description,
        },
        objective: {
          title: config.objective.title.trim() || DEFAULT_LANDING_PAGE_CONFIG.objective.title,
          description: config.objective.description.trim() || DEFAULT_LANDING_PAGE_CONFIG.objective.description,
        },
        modules: config.modules.map((m) => ({
          id: m.id,
          title: m.title.trim() || m.id,
          description: m.description.trim(),
          enabled: typeof m.enabled === "boolean" ? m.enabled : true,
        })),
        commercialModels: {
          title: config.commercialModels.title.trim() || DEFAULT_LANDING_PAGE_CONFIG.commercialModels.title,
          description: config.commercialModels.description.trim() || DEFAULT_LANDING_PAGE_CONFIG.commercialModels.description,
          models: config.commercialModels.models.map((cm) => ({
            id: cm.id,
            title: cm.title.trim() || cm.id,
            subtitle: cm.subtitle.trim(),
            description: cm.description.trim(),
            enabled: typeof cm.enabled === "boolean" ? cm.enabled : true,
          })),
        },
        benefits: {
          title: config.benefits.title.trim() || DEFAULT_LANDING_PAGE_CONFIG.benefits.title,
          description: config.benefits.description.trim() || DEFAULT_LANDING_PAGE_CONFIG.benefits.description,
          items: config.benefits.items.map((b) => ({
            id: b.id,
            title: b.title.trim() || b.id,
            description: b.description.trim(),
            enabled: typeof b.enabled === "boolean" ? b.enabled : true,
          })),
        },
        contact: {
          title: config.contact.title.trim() || DEFAULT_LANDING_PAGE_CONFIG.contact.title,
          description: config.contact.description.trim() || DEFAULT_LANDING_PAGE_CONFIG.contact.description,
        },
        finalCta: {
          title: config.finalCta.title.trim() || DEFAULT_LANDING_PAGE_CONFIG.finalCta.title,
          subtitle: config.finalCta.subtitle.trim() || DEFAULT_LANDING_PAGE_CONFIG.finalCta.subtitle,
        },
        footer: {
          description: config.footer.description.trim() || DEFAULT_LANDING_PAGE_CONFIG.footer.description,
          copyright: config.footer.copyright.trim() || DEFAULT_LANDING_PAGE_CONFIG.footer.copyright,
        },
        updatedAt: new Date().toISOString(),
        updatedBy: updatedByEmail || "admin",
      };

      await setDoc(docRef, payload, { merge: true });
      return true;
    } catch (error) {
      console.error(
        "[LandingPageConfigService] Erro ao salvar configuração da Landing Page no Firestore:",
        error
      );
      throw error;
    }
  },
};
